import * as React from "react";
import classnames from "classnames";
import { connect } from "react-redux";

import APIClient from "web_modules/APIClient";
import Header, { HeaderLink } from "web_modules/shared/Header";
import Spinner from "web_modules/shared/Spinner";
import browserHistory from "web_modules/redux/history";
import { AppShape, HLHLContactRole, UserTypes, Role } from "@eave/shared/types/api";
import { endSession, getSessionToken } from "web_modules/shared/session";
import { setUserContext } from "web_modules/utils/sentry";
import saveHLHLContactRole from "./actions";

const styles = require("./styles.module.scss");

const trusOrigin =
  /((https:\/\/)((integration|staging|hotfix|homeloans|www)\.)?trus\.(io|local))|(http:\/\/localhost:8080)/g;

require("../../../assets/stylesheets/components/underwriter_dashboard/underwriter_dashboard.scss");

declare let window: any;

interface Props {
  children: React.ReactNode;
  dispatch: Function;
  loading: boolean;
  location: any;
  app: AppShape | null;
  titleLink: HeaderLink;
  headerLinks?: HeaderLink[];

  persistHLHLContactRole: Function;
}

class LoggedInContainer extends React.Component<Props, any> {
  constructor(props) {
    super(props);

    this.onToggleNavExpanded = this.onToggleNavExpanded.bind(this);
    this.logOut = this.logOut.bind(this);

    this.state = {
      loggedIn: false,
      navExpanded: true,
    };
  }

  async componentDidMount() {
    const { persistHLHLContactRole } = this.props;
    // If the logout param is present log out the user
    if (new URL(window.location.href).search.includes("logout")) {
      this.logOut();
      return;
    }

    document.body.classList.add("underwriter", styles.container);
    if (!getSessionToken()) {
      let nextURL = "/operators/login";
      const { location } = this.props;
      const intendedDestination = location.pathname + location.search + location.hash;
      if (intendedDestination !== "/") {
        nextURL = `${nextURL}?redirect=${encodeURIComponent(intendedDestination)}`;
      }
      browserHistory.push(nextURL);
      return;
    }

    const sessionResp = (await APIClient.get("/session")) as {
      first_name: string;
      last_name: string;
      picture: string;
      user_id: number;
      user_type: UserTypes;
      roles: Role[];
      hlhl_contact_role: string;
    }; // TEMP: ideally it would be better if APIClient had this definition baked in

    // Sets up Sentry User Context:
    setUserContext(sessionResp);
    persistHLHLContactRole(sessionResp.hlhl_contact_role);

    this.setState({
      loggedIn: true,
      ...sessionResp,
    });

    // If sessionResp isn't valid (doesn't have roles, in particular), don't load loan apps, just log out
    // for expired sessions, we get a 403 and sessionResp is an empty object
    if (sessionResp.roles === undefined) {
      this.logOut();
    }
  }

  onToggleNavExpanded() {
    this.setState(prevState => ({
      ...prevState,
      navExpanded: !this.state.navExpanded,
    }));
  }

  pusher: any;

  logOut() {
    // If we want to logout from a SHA url we need to redirect to the
    // regular url so that Google OAuth will recognize where we are coming from
    if (!window.location.origin.match(trusOrigin)) {
      const loc = new URL(window.location.href);
      loc.searchParams.set("logout", "true");

      // remove the sha from the host name
      loc.hostname = loc.hostname.split(".").slice(1).join(".");

      window.location.href = loc.toString();
      return;
    }

    endSession();
    window.gapi.load("auth2", () => {
      window.gapi.auth2.init().then(() => {
        const auth2 = window.gapi.auth2.getAuthInstance();
        auth2.signOut().then(() => {
          this.setState({ loggedIn: false });
          browserHistory.push("/operators/login");
        });
      });
    });
  }

  render() {
    const { app, loading, children, titleLink, headerLinks } = this.props;
    const { loggedIn, navExpanded, first_name } = this.state;

    if (!loggedIn) {
      return <Spinner active={!loggedIn} />;
    }

    // make sure we're on a loan page (handles hiding if expanded and we switch to non-loan page)
    const loanAppInContext = !!(app && app.id);
    const containerClasses = classnames({
      [styles.container]: true,
      [styles.withNavSidebarMargin]: loanAppInContext && navExpanded,
    });

    return (
      <div className={containerClasses}>
        <Header
          app={app}
          titleLink={titleLink}
          headerLinks={headerLinks}
          navExpanded={navExpanded}
          firstName={first_name}
          onToggleNavExpanded={this.onToggleNavExpanded}
          onLogOut={this.logOut}
        />
        {children}
        <Spinner active={loading} />
      </div>
    );
  }
}

const mapStateToProps = state => {
  const { loading, loanApps } = state.operator;
  const { location } = state.routing;

  let app = null;
  if (location) {
    const { pathname } = location;
    const pathnameId = pathname.match(/\d+/);
    const appId = pathnameId ? pathnameId[0] : null;
    app = loanApps ? loanApps.find((a: AppShape) => a.id === +appId) : null;
  }

  return {
    loading,
    location,
    app,
  };
};

interface OwnProps {
  titleLink: HeaderLink;
  headerLinks?: HeaderLink[];
}

const mapDispatchToProps = dispatch => ({
  persistHLHLContactRole: (role: HLHLContactRole) => dispatch(saveHLHLContactRole(role)),
});

export default connect<ReturnType<typeof mapStateToProps>, {}, OwnProps>(
  mapStateToProps,
  mapDispatchToProps,
)(LoggedInContainer);
